/**
 * The WebCrypto seam for document media (encrypted images in documents).
 *
 * Everything the renderer or a test touches crypto-wise goes THROUGH this
 * module, for two reasons:
 *  - jsdom has no crypto.subtle, so tests jest.mock() this file instead of
 *    stubbing WebCrypto (same reason chatCrypto is never imported directly by
 *    UI-state tests);
 *  - the wire format must stay chat's exact one — 12-byte random IV prepended
 *    to AES-256-GCM ciphertext, one opaque application/octet-stream blob — so
 *    decryption DELEGATES to chatCrypto.decryptBuffer rather than reimplementing
 *    it and drifting.
 *
 * Unlike chat (per-CHANNEL key from the server), a document image's key is
 * PER-ATTACHMENT and travels inside the Lexical node JSON (`k`, base64 32
 * bytes — see lexicalNodes/DocImageNode.js). The document body itself is the
 * access boundary: whoever can read the doc gets the key with it, and the
 * bucket only ever stores ciphertext at an unguessable public-read URL.
 */
import { decryptBuffer, encryptFile } from './chatCrypto';

// base64 key -> Promise<CryptoKey>. Imported non-extractable and DECRYPT-ONLY
// (encryption — encryptMediaFile below — always uses its own freshly
// generated key, never one re-imported from a document).
const importedKeys = new Map();

/** Memoized `crypto.subtle.importKey` of a base64 32-byte AES-GCM key. */
export function importRawMediaKey(b64) {
  if (importedKeys.has(b64)) return importedKeys.get(b64);
  let raw;
  try {
    raw = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  } catch {
    // Malformed base64 is not memoized — nothing to hold onto.
    return Promise.reject(new Error('invalid media key'));
  }
  const promise = crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['decrypt']);
  importedKeys.set(b64, promise);
  // Drop failed imports (e.g. wrong key length) so the map holds only usable
  // keys; the caller still sees the original rejection.
  promise.catch(() => importedKeys.delete(b64));
  return promise;
}

/** Ciphertext ArrayBuffer (+ its node's base64 key) -> decrypted Blob. */
export async function decryptMediaBuffer(keyB64, buf, mime) {
  const key = await importRawMediaKey(keyB64);
  return decryptBuffer(key, buf, mime);
}

/**
 * Encrypt a File/Blob under its fresh per-attachment key (base64) ->
 * Blob([iv, ciphertext]) ready for upload — chat's EXACT wire format, by
 * delegation to chatCrypto.encryptFile rather than a reimplementation.
 *
 * The key is imported ENCRYPT-ONLY and deliberately NOT memoized: unlike the
 * decrypt map above (one document key decrypting many renders), an encryption
 * key is generated fresh per attachment and used exactly once here.
 */
export async function encryptMediaFile(keyB64, file) {
  let raw;
  try {
    raw = Uint8Array.from(atob(keyB64), (c) => c.charCodeAt(0));
  } catch {
    throw new Error('invalid media key');
  }
  const key = await crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, ['encrypt']);
  return encryptFile(key, file);
}

/**
 * A fresh per-attachment key: 32 random bytes, base64 — one per staged
 * attachment (DocMediaPlugin.stageAndInsert), never reused across files.
 */
export function generateMediaKeyB64() {
  const raw = crypto.getRandomValues(new Uint8Array(32));
  let s = '';
  for (let i = 0; i < raw.length; i += 1) s += String.fromCharCode(raw[i]);
  return btoa(s);
}
